import { z } from "zod";

const comparisonOperatorSchema = z.enum(["eq", "gte", "lte"]);

const optionalBoolean = z
    .union([z.boolean(), z.stringbool()])
    .optional()
    .catch(undefined);

const arrayOrScalar = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .union([schema, z.array(schema)])
        .transform((value) => (Array.isArray(value) ? value : [value]))
        .optional()
        .catch(undefined);

export const ratingFilterSchema = z
    .object({
        value: z.enum(["1", "2", "3", "4", "5"]),
        operator: comparisonOperatorSchema.default("eq"),
    })
    .loose();

export type RatingFilter = z.infer<typeof ratingFilterSchema>;

export const searchSortSchema = z.enum([
    "best-match",
    "nearest-first",
    "newest-listings",
    "oldest-listings",
    "most-popular",
    "cutest-first",
]);

export type SearchSortValue = z.infer<typeof searchSortSchema>;

export const bestMatchPreferencesSchema = z
    .object({
        hypoallergenic: optionalBoolean,
        goodWithKids: optionalBoolean,
        goodWithDogs: optionalBoolean,
        goodWithCats: optionalBoolean,
        easyToTrain: optionalBoolean,
        lowShedding: optionalBoolean,
        active: optionalBoolean,
        lowerHealthRisk: optionalBoolean,
    })
    .loose();

export type BestMatchPreferences = z.infer<typeof bestMatchPreferencesSchema>;

export const providerTypeSchema = z.enum(["breeder", "shelter_rescue"]);

export type ProviderTypeFilter = z.infer<typeof providerTypeSchema>;

export const breedTypeSchema = z.enum(["purebred", "mixed"]);
export const sexSchema = z.enum(["male", "female"]);
export const sizeSchema = z.enum(["toy", "small", "medium", "large", "giant"]);
export const coatTypeSchema = z.enum([
    "smooth",
    "short",
    "medium",
    "long",
    "wire",
    "curly",
    "double",
    "hairless",
]);
export const sheddingLevelSchema = z.enum([
    "none",
    "minimal",
    "moderate",
    "heavy",
    "seasonal_blowout",
]);

const coordinateSchema = z.coerce.number().catch(Number.NaN);

const optionalNumber = z.coerce.number().optional().catch(undefined);

const discoveryFilterFields = {
    feedContext: z.string().optional(),
    maxDistanceMiles: optionalNumber,
    hardRadius: optionalBoolean,
    breedSlugs: z.array(z.string()).optional(),
    dogName: z.string().optional(),
    breedType: breedTypeSchema.optional(),
    providerName: z.string().optional(),
    providerType: providerTypeSchema.optional(),
    size: arrayOrScalar(sizeSchema),
    coatType: arrayOrScalar(coatTypeSchema),
    sheddingLevel: arrayOrScalar(sheddingLevelSchema),
    hypoallergenic: optionalBoolean,
    priceMin: optionalNumber,
    priceMax: optionalNumber,
    dobMin: optionalNumber,
    dobMax: optionalNumber,
    sex: sexSchema.optional(),
    friendlinessChildren: arrayOrScalar(ratingFilterSchema),
    friendlinessDogs: arrayOrScalar(ratingFilterSchema),
    friendlinessCats: arrayOrScalar(ratingFilterSchema),
    energyLevel: arrayOrScalar(ratingFilterSchema),
    trainability: arrayOrScalar(ratingFilterSchema),
    healthIssuesRisk: arrayOrScalar(ratingFilterSchema),
    excludeLitters: optionalBoolean,
    sortBy: searchSortSchema.optional(),
    bestMatchPreferences: bestMatchPreferencesSchema.optional(),
} as const;

export const discoverySearchParamsSchema = z
    .object({
        lat: coordinateSchema,
        lng: coordinateSchema,
        q: z.string().optional().catch(undefined),
        ...discoveryFilterFields,
        offset: optionalNumber,
        page: optionalNumber,
        limit: optionalNumber,
    })
    .loose();

export type DiscoverySearchParams = z.infer<typeof discoverySearchParamsSchema>;

const doomscrollSearchParamsSchema = z
    .object({
        lat: coordinateSchema,
        lng: coordinateSchema,
        ...discoveryFilterFields,
    })
    .loose();

export type DoomscrollSearchParams = z.infer<
    typeof doomscrollSearchParamsSchema
>;

const doomscrollSearchDirectionSchema = z.enum(["around", "next", "prev"]);

export const doomscrollSearchInputSchema = z
    .object({
        params: doomscrollSearchParamsSchema,
        listingId: z.string(),
        direction: doomscrollSearchDirectionSchema.optional(),
        limit: optionalNumber,
    })
    .loose();

export type DoomscrollSearchInput = z.infer<typeof doomscrollSearchInputSchema>;

export const feedParamsSchema = z
    .object({
        lat: coordinateSchema,
        lng: coordinateSchema,
        maxDistanceMiles: optionalNumber,
        page: optionalNumber,
        limit: optionalNumber,
    })
    .loose();

export type FeedParams = z.infer<typeof feedParamsSchema>;
